import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, styled, useTheme } from '@mui/material';

import { Skeleton, Typography } from '@zendesk/forethought-elements';
import { DeflectionInsightCell } from '../components/DeflectionInsightCell';
import { NA } from '../constants';
import { Conversation } from 'src/services/apiInterfaces';
import { isDeflectionInsightsProcessing } from 'src/utils/solve/deflectionInsightsUtils';

export const DeflectionInsightsData = ({
  isLoading = false,
  selectedConversation,
  showTopDivider = true,
}: {
  isLoading?: boolean;
  selectedConversation: Conversation;
  showTopDivider?: boolean;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const {
    dropoff_rating,
    dropoff_reasoning = NA,
    relevance_rating,
    relevance_reasoning = NA,
  } = selectedConversation;
  const isProcessing = isDeflectionInsightsProcessing({
    hasDeflectionInsights: !!relevance_rating,
    isDeflected: selectedConversation.deflected,
    modifiedDate: selectedConversation.modified_date,
  });

  const DeflectionInsight = ({
    badge,
    label,
    showDivider = true,
    value,
  }: {
    badge: React.ReactNode;
    label: string;
    showDivider?: boolean;
    value: string | null;
  }) => {
    return (
      <>
        {showDivider && <Divider />}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}
        >
          <Typography color={palette.colors.grey[600]} variant='font12Medium'>
            {t('common.labelWithColon', { label })}
          </Typography>
          {badge}
        </Box>
        {!isProcessing && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            <Typography color={palette.colors.grey[600]} variant='font12Medium'>
              {t('deflectionInsightData.labelReasoning', { label })}
            </Typography>
            <Typography
              color={
                relevance_reasoning === NA
                  ? palette.colors.grey[500]
                  : palette.colors.grey[600]
              }
              variant='font12Medium'
            >
              {isLoading ? <Skeleton width={30} /> : value}
            </Typography>
          </Box>
        )}
      </>
    );
  };

  return (
    <>
      <DeflectionInsight
        badge={
          <DeflectionInsightCell
            isProcessing={isProcessing}
            metric={relevance_rating}
          />
        }
        label={t('deflectionInsightData.relevance')}
        showDivider={showTopDivider}
        value={relevance_reasoning}
      />
      <DeflectionInsight
        badge={
          <DeflectionInsightCell
            isProcessing={isProcessing}
            metric={dropoff_rating}
          />
        }
        label={t('deflectionInsightData.userEngagement')}
        value={dropoff_reasoning}
      />
    </>
  );
};

const Divider = styled('hr')`
  border-width: 0;
  border-bottom-width: thin;
  border-color: ${({ theme }) => theme.palette.colors.slate[200]};
  width: 100%;
  margin-top: 8px;
  margin-bottom: 8px;
`;
